import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Settings2 } from 'lucide-react';
import { getMyAiCredits } from '../../services/api/implementation/impl';
import { formatAiCreditBalance } from './AiUiShared';

const INTEGRATION_PATH = '/builder/integration?openAi=1';

export default function AiCreditsRechargeAction({ aiCredits, compact = false }) {
  const { data: myCredits } = useQuery({
    queryKey: ['myAiCredits'],
    queryFn: getMyAiCredits,
    staleTime: 60_000
  });

  const canManage = myCredits?.canManage;
  const provider = myCredits?.provider ?? aiCredits?.provider;
  const usingOwnKey = provider?.useOwnKey;
  const ownKeyMissing = usingOwnKey && !provider?.apiKeyConfigured;
  const ownKeyActive = usingOwnKey && provider?.apiKeyConfigured;

  const showAction =
    aiCredits?.enforcementEnabled &&
    !ownKeyActive &&
    (aiCredits?.depleted ||
      aiCredits?.lowBalance ||
      !aiCredits?.llmEnabled ||
      ownKeyMissing);

  if (!showAction) return null;

  if (canManage) {
    const balance = formatAiCreditBalance(aiCredits);
    const helperText = ownKeyMissing
      ? 'Paste your OpenAI API key to resume AI features. Billing goes to your provider account.'
      : usingOwnKey
        ? 'Manage your API key and team token limits.'
        : aiCredits?.depleted
          ? `Platform credits (${balance}) are used up. Connect your own OpenAI key to continue without waiting on Housingram.`
          : 'Connect your own OpenAI key or adjust team limits before credits run out.';

    return (
      <div className={`rounded-xl border border-teal-200 bg-teal-50/60 p-3 ${compact ? '' : 'mt-2'}`}>
        <p className="text-xs leading-relaxed text-teal-950">{helperText}</p>
        <Link
          to={INTEGRATION_PATH}
          className={`inline-flex items-center gap-1.5 rounded-lg bg-[#1E4A53] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#163a42] ${compact ? 'mt-2' : 'mt-2'}`}>
          <Settings2 className="h-3.5 w-3.5" />
          Open AI integration
        </Link>
      </div>
    );
  }

  return (
    <p className={`text-xs leading-relaxed text-amber-900 ${compact ? '' : 'mt-2'}`}>
      Ask your builder account owner to configure AI under{' '}
      <span className="font-semibold">Integrations → AI Integration</span> (API key or team limits).
    </p>
  );
}
